import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useAsyncResource } from '../hooks/useAsyncResource'
import { repositoryAPI } from '../services/api'
import CertifiedBadge from '../components/CertifiedBadge'
import ShareLinkButton from '../components/ShareLinkButton'
import { getDeviceTypeLabel } from '../utils/deviceType'
import './Page.css'
import './BrowseRepositories.css'

const DEBOUNCE_MS = 250

function BrowseRepositories() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedManufacturer, setSelectedManufacturer] = useState('')
  const [selectedDeviceType, setSelectedDeviceType] = useState('')
  const [selectedFormFactor, setSelectedFormFactor] = useState('')
  const [selectedLicense, setSelectedLicense] = useState('')
  const [sortBy, setSortBy] = useState('updated')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  // Searchable manufacturer combobox (scalable: only fetches matches, max 50)
  const [manufacturerInput, setManufacturerInput] = useState('')
  const [manufacturerDropdownOpen, setManufacturerDropdownOpen] = useState(false)
  const [manufacturerOptions, setManufacturerOptions] = useState([])
  const [manufacturerLoading, setManufacturerLoading] = useState(false)
  const manufacturerDebounceRef = useRef(null)
  const manufacturerBlurRef = useRef(null)

  // Searchable license combobox
  const [licenseInput, setLicenseInput] = useState('')
  const [licenseDropdownOpen, setLicenseDropdownOpen] = useState(false)
  const [licenseOptions, setLicenseOptions] = useState([])
  const [licenseLoading, setLicenseLoading] = useState(false)
  const licenseDebounceRef = useRef(null)
  const licenseBlurRef = useRef(null)

  // Searchable device type combobox
  const [deviceTypeInput, setDeviceTypeInput] = useState('')
  const [deviceTypeDropdownOpen, setDeviceTypeDropdownOpen] = useState(false)
  const [deviceTypeOptions, setDeviceTypeOptions] = useState([])
  const [deviceTypeLoading, setDeviceTypeLoading] = useState(false)
  const deviceTypeDebounceRef = useRef(null)
  const deviceTypeBlurRef = useRef(null)

  // Searchable form factor combobox
  const [formFactorInput, setFormFactorInput] = useState('')
  const [formFactorDropdownOpen, setFormFactorDropdownOpen] = useState(false)
  const [formFactorOptions, setFormFactorOptions] = useState([])
  const [formFactorLoading, setFormFactorLoading] = useState(false)
  const formFactorDebounceRef = useRef(null)
  const formFactorBlurRef = useRef(null)

  // State for API data
  const [filterOptions] = useState({
    sortOptions: [
      { value: 'newest', label: 'Newest First' },
      { value: 'oldest', label: 'Oldest First' },
      { value: 'updated', label: 'Recently Updated' },
      { value: 'name', label: 'Name (A-Z)' },
      { value: 'founder_repos', label: 'Founder Repos' },
    ]
  })
  const fetchManufacturerOptions = useCallback(async (q) => {
    setManufacturerLoading(true)
    try {
      const filters = await repositoryAPI.getFilters({ manufacturer_q: q ?? '' })
      setManufacturerOptions(filters.manufacturers || [])
    } catch {
      setManufacturerOptions([])
    } finally {
      setManufacturerLoading(false)
    }
  }, [])

  const fetchLicenseOptions = useCallback(async (q) => {
    setLicenseLoading(true)
    try {
      const filters = await repositoryAPI.getFilters({ license_q: q ?? '' })
      setLicenseOptions(filters.licenses || [])
    } catch {
      setLicenseOptions([])
    } finally {
      setLicenseLoading(false)
    }
  }, [])

  const fetchDeviceTypeOptions = useCallback(async (q) => {
    setDeviceTypeLoading(true)
    try {
      const filters = await repositoryAPI.getFilters({ device_type_q: q ?? '' })
      setDeviceTypeOptions(filters.device_types || [])
    } catch {
      setDeviceTypeOptions([])
    } finally {
      setDeviceTypeLoading(false)
    }
  }, [])

  const fetchFormFactorOptions = useCallback(async (q) => {
    setFormFactorLoading(true)
    try {
      const filters = await repositoryAPI.getFilters({ form_factor_q: q ?? '' })
      setFormFactorOptions(filters.form_factors || [])
    } catch {
      setFormFactorOptions([])
    } finally {
      setFormFactorLoading(false)
    }
  }, [])

  const { data: listResponse, loading, error } = useAsyncResource(
    (signal) =>
      repositoryAPI.list({
        page: currentPage,
        page_size: itemsPerPage,
        sort: sortBy,
        search: searchQuery || undefined,
        manufacturer: selectedManufacturer || undefined,
        device_type: selectedDeviceType || undefined,
        form_factor: selectedFormFactor || undefined,
        license: selectedLicense || undefined,
        signal,
      }),
    [currentPage, itemsPerPage, sortBy, searchQuery, selectedManufacturer, selectedDeviceType, selectedFormFactor, selectedLicense]
  )

  const repositories = listResponse?.repositories || []
  const total = listResponse?.total || 0
  const totalPages = listResponse?.total_pages || 1

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, selectedManufacturer, selectedDeviceType, selectedFormFactor, selectedLicense])

  // Manufacturer combobox: fetch options when dropdown opens or input changes (debounced)
  useEffect(() => {
    if (!manufacturerDropdownOpen) return
    if (manufacturerDebounceRef.current) clearTimeout(manufacturerDebounceRef.current)
    manufacturerDebounceRef.current = setTimeout(() => {
      fetchManufacturerOptions(manufacturerInput)
      manufacturerDebounceRef.current = null
    }, DEBOUNCE_MS)
    return () => {
      if (manufacturerDebounceRef.current) clearTimeout(manufacturerDebounceRef.current)
    }
  }, [manufacturerDropdownOpen, manufacturerInput, fetchManufacturerOptions])

  // License combobox: fetch options when dropdown opens or input changes (debounced)
  useEffect(() => {
    if (!licenseDropdownOpen) return
    if (licenseDebounceRef.current) clearTimeout(licenseDebounceRef.current)
    licenseDebounceRef.current = setTimeout(() => {
      fetchLicenseOptions(licenseInput)
      licenseDebounceRef.current = null
    }, DEBOUNCE_MS)
    return () => {
      if (licenseDebounceRef.current) clearTimeout(licenseDebounceRef.current)
    }
  }, [licenseDropdownOpen, licenseInput, fetchLicenseOptions])

  // Device type combobox
  useEffect(() => {
    if (!deviceTypeDropdownOpen) return
    if (deviceTypeDebounceRef.current) clearTimeout(deviceTypeDebounceRef.current)
    deviceTypeDebounceRef.current = setTimeout(() => {
      fetchDeviceTypeOptions(deviceTypeInput)
      deviceTypeDebounceRef.current = null
    }, DEBOUNCE_MS)
    return () => {
      if (deviceTypeDebounceRef.current) clearTimeout(deviceTypeDebounceRef.current)
    }
  }, [deviceTypeDropdownOpen, deviceTypeInput, fetchDeviceTypeOptions])

  // Form factor combobox
  useEffect(() => {
    if (!formFactorDropdownOpen) return
    if (formFactorDebounceRef.current) clearTimeout(formFactorDebounceRef.current)
    formFactorDebounceRef.current = setTimeout(() => {
      fetchFormFactorOptions(formFactorInput)
      formFactorDebounceRef.current = null
    }, DEBOUNCE_MS)
    return () => {
      if (formFactorDebounceRef.current) clearTimeout(formFactorDebounceRef.current)
    }
  }, [formFactorDropdownOpen, formFactorInput, fetchFormFactorOptions])

  const handleRepositoryClick = (e, repoSlug) => {
    e.preventDefault()
    navigate(`/repositories/${repoSlug}`)
  }

  const handleCreateNewRepository = () => {
    if (!isAuthenticated) {
      navigate('/', {
        state: {
          showLogin: true,
          redirectTo: '/dashboard',
        },
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    navigate('/dashboard', { state: { openNewRepository: true } })
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedManufacturer('')
    setManufacturerInput('')
    setManufacturerDropdownOpen(false)
    setSelectedDeviceType('')
    setDeviceTypeInput('')
    setDeviceTypeDropdownOpen(false)
    setSelectedFormFactor('')
    setFormFactorInput('')
    setFormFactorDropdownOpen(false)
    setSelectedLicense('')
    setLicenseInput('')
    setLicenseDropdownOpen(false)
    setSortBy('updated')
    setCurrentPage(1)
  }

  const hasActiveFilters = searchQuery || selectedManufacturer || selectedDeviceType || 
    selectedFormFactor || selectedLicense

  return (
    <div className="page-container browse-repositories">
      <div className="browse-header">
        <div className="browse-header-row">
          <h1>Browse Repositories</h1>
          <button type="button" className="btn btn-secondary" onClick={handleCreateNewRepository}>
            Create new repository
          </button>
        </div>
        <p className="browse-subtitle">
          {loading ? 'Loading...' : `${total} ${total === 1 ? 'repository' : 'repositories'} found`}
        </p>
      </div>

      <div className="browse-layout">
        {/* Filters Sidebar */}
        <aside className="filters-sidebar">
          <div className="filters-header">
            <h2>Filters</h2>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="clear-filters-btn">
                Clear All
              </button>
            )}
          </div>

          {/* Search */}
          <div className="filter-group">
            <label className="filter-label">Search</label>
            <input
              type="text"
              placeholder="Search repositories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="filter-input"
            />
          </div>

          {/* Manufacturer: searchable combobox (scales to many manufacturers) */}
          <div className="filter-group filter-combobox">
            <label className="filter-label">Manufacturer</label>
            <div className="combobox-wrap">
              <input
                type="text"
                placeholder="Type to search manufacturers..."
                value={manufacturerDropdownOpen ? manufacturerInput : (selectedManufacturer || '')}
                onChange={(e) => {
                  setManufacturerInput(e.target.value)
                  setSelectedManufacturer('')
                  setManufacturerDropdownOpen(true)
                }}
                onFocus={() => {
                  setManufacturerDropdownOpen(true)
                  if (manufacturerBlurRef.current) clearTimeout(manufacturerBlurRef.current)
                }}
                onBlur={() => {
                  manufacturerBlurRef.current = setTimeout(() => setManufacturerDropdownOpen(false), 150)
                }}
                className="filter-input combobox-input"
                autoComplete="off"
              />
              {selectedManufacturer && !manufacturerDropdownOpen && (
                <button
                  type="button"
                  className="combobox-clear"
                  onClick={() => { setSelectedManufacturer(''); setManufacturerInput('') }}
                  aria-label="Clear manufacturer"
                >
                  ×
                </button>
              )}
              {manufacturerDropdownOpen && (
                <ul className="combobox-dropdown" role="listbox">
                  {manufacturerLoading ? (
                    <li className="combobox-item combobox-loading">Loading...</li>
                  ) : manufacturerOptions.length === 0 ? (
                    <li className="combobox-item combobox-empty">No manufacturers match</li>
                  ) : (
                    manufacturerOptions.map((mfg) => (
                      <li
                        key={mfg}
                        role="option"
                        className="combobox-item"
                        onMouseDown={(e) => { e.preventDefault(); setSelectedManufacturer(mfg); setManufacturerInput(''); setManufacturerDropdownOpen(false) }}
                      >
                        {mfg}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>

          {/* Device Type: searchable combobox */}
          <div className="filter-group filter-combobox">
            <label className="filter-label">Device Type</label>
            <div className="combobox-wrap">
              <input
                type="text"
                placeholder="Type to search device types..."
                value={deviceTypeDropdownOpen ? deviceTypeInput : (selectedDeviceType || '')}
                onChange={(e) => {
                  setDeviceTypeInput(e.target.value)
                  setSelectedDeviceType('')
                  setDeviceTypeDropdownOpen(true)
                }}
                onFocus={() => {
                  setDeviceTypeDropdownOpen(true)
                  if (deviceTypeBlurRef.current) clearTimeout(deviceTypeBlurRef.current)
                }}
                onBlur={() => {
                  deviceTypeBlurRef.current = setTimeout(() => setDeviceTypeDropdownOpen(false), 150)
                }}
                className="filter-input combobox-input"
                autoComplete="off"
              />
              {selectedDeviceType && !deviceTypeDropdownOpen && (
                <button
                  type="button"
                  className="combobox-clear"
                  onClick={() => { setSelectedDeviceType(''); setDeviceTypeInput('') }}
                  aria-label="Clear device type"
                >
                  ×
                </button>
              )}
              {deviceTypeDropdownOpen && (
                <ul className="combobox-dropdown" role="listbox">
                  {deviceTypeLoading ? (
                    <li className="combobox-item combobox-loading">Loading...</li>
                  ) : deviceTypeOptions.length === 0 ? (
                    <li className="combobox-item combobox-empty">No device types match</li>
                  ) : (
                    deviceTypeOptions.map((type) => (
                      <li
                        key={type}
                        role="option"
                        className="combobox-item"
                        onMouseDown={(e) => { e.preventDefault(); setSelectedDeviceType(type); setDeviceTypeInput(''); setDeviceTypeDropdownOpen(false) }}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>

          {/* Form Factor: searchable combobox */}
          <div className="filter-group filter-combobox">
            <label className="filter-label">Form Factor</label>
            <div className="combobox-wrap">
              <input
                type="text"
                placeholder="Type to search form factors..."
                value={formFactorDropdownOpen ? formFactorInput : (selectedFormFactor || '')}
                onChange={(e) => {
                  setFormFactorInput(e.target.value)
                  setSelectedFormFactor('')
                  setFormFactorDropdownOpen(true)
                }}
                onFocus={() => {
                  setFormFactorDropdownOpen(true)
                  if (formFactorBlurRef.current) clearTimeout(formFactorBlurRef.current)
                }}
                onBlur={() => {
                  formFactorBlurRef.current = setTimeout(() => setFormFactorDropdownOpen(false), 150)
                }}
                className="filter-input combobox-input"
                autoComplete="off"
              />
              {selectedFormFactor && !formFactorDropdownOpen && (
                <button
                  type="button"
                  className="combobox-clear"
                  onClick={() => { setSelectedFormFactor(''); setFormFactorInput('') }}
                  aria-label="Clear form factor"
                >
                  ×
                </button>
              )}
              {formFactorDropdownOpen && (
                <ul className="combobox-dropdown" role="listbox">
                  {formFactorLoading ? (
                    <li className="combobox-item combobox-loading">Loading...</li>
                  ) : formFactorOptions.length === 0 ? (
                    <li className="combobox-item combobox-empty">No form factors match</li>
                  ) : (
                    formFactorOptions.map((factor) => (
                      <li
                        key={factor}
                        role="option"
                        className="combobox-item"
                        onMouseDown={(e) => { e.preventDefault(); setSelectedFormFactor(factor); setFormFactorInput(''); setFormFactorDropdownOpen(false) }}
                      >
                        {factor.charAt(0).toUpperCase() + factor.slice(1)}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>

          {/* License: searchable combobox */}
          <div className="filter-group filter-combobox">
            <label className="filter-label">License</label>
            <div className="combobox-wrap">
              <input
                type="text"
                placeholder="Type to search licenses..."
                value={licenseDropdownOpen ? licenseInput : (selectedLicense || '')}
                onChange={(e) => {
                  setLicenseInput(e.target.value)
                  setSelectedLicense('')
                  setLicenseDropdownOpen(true)
                }}
                onFocus={() => {
                  setLicenseDropdownOpen(true)
                  if (licenseBlurRef.current) clearTimeout(licenseBlurRef.current)
                }}
                onBlur={() => {
                  licenseBlurRef.current = setTimeout(() => setLicenseDropdownOpen(false), 150)
                }}
                className="filter-input combobox-input"
                autoComplete="off"
              />
              {selectedLicense && !licenseDropdownOpen && (
                <button
                  type="button"
                  className="combobox-clear"
                  onClick={() => { setSelectedLicense(''); setLicenseInput('') }}
                  aria-label="Clear license"
                >
                  ×
                </button>
              )}
              {licenseDropdownOpen && (
                <ul className="combobox-dropdown" role="listbox">
                  {licenseLoading ? (
                    <li className="combobox-item combobox-loading">Loading...</li>
                  ) : licenseOptions.length === 0 ? (
                    <li className="combobox-item combobox-empty">No licenses match</li>
                  ) : (
                    licenseOptions.map((lic) => (
                      <li
                        key={lic}
                        role="option"
                        className="combobox-item"
                        onMouseDown={(e) => { e.preventDefault(); setSelectedLicense(lic); setLicenseInput(''); setLicenseDropdownOpen(false) }}
                      >
                        {lic}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="browse-content">
          {/* Sort and View Options */}
          <div className="browse-toolbar">
            <div className="sort-controls">
              <label htmlFor="sort-select" className="sort-label">Sort by:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                {filterOptions.sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="error-message">
              <p>Error loading repositories: {error}</p>
              <button onClick={() => window.location.reload()} className="retry-btn">
                Retry
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && !error && (
            <div className="loading-state">
              <p>Loading repositories...</p>
            </div>
          )}

          {/* Repository Grid */}
          {!loading && !error && (
            <div className="repositories-grid">
              {repositories.length > 0 ? (
                repositories.map(repo => {
                  const repoSlug = repo.slug || repo.id
                  const repoPath = repoSlug ? `/repositories/${repoSlug}` : null
                  return (
                  <div key={repo.id} className="repository-card-with-share">
                    <ShareLinkButton path={repoPath} variant="card" />
                    <a
                      href={repoPath || '#'}
                      onClick={(e) => repoSlug && handleRepositoryClick(e, repoSlug)}
                      className="repository-card"
                    >
                    <div className="repository-card-header">
                      <div className="repository-title-section">
                        <h3>{repo.title}</h3>
                        <span className={`visibility-badge visibility-${repo.visibility}`}>
                          {repo.visibility}
                        </span>
                      </div>
                      {getDeviceTypeLabel(repo) && (
                        <div className="device-type-badge">{getDeviceTypeLabel(repo)}</div>
                      )}
                    </div>

                    <div className="repository-meta-row">
                      {repo.manufacturer && (
                        <>
                          <span className="repository-manufacturer">{repo.manufacturer}</span>
                          {repo.year && <span className="repository-year">• {repo.year}</span>}
                          {repo.form_factor && (
                            <span className="repository-form-factor">• {repo.form_factor}</span>
                          )}
                        </>
                      )}
                    </div>

                    {repo.description && (
                      <p className="repository-description">{repo.description}</p>
                    )}

                    {repo.tags && repo.tags.length > 0 && (
                      <div className="repository-tags">
                        {repo.tags.slice(0, 4).map(tag => (
                          <span key={tag} className="tag-chip">{tag}</span>
                        ))}
                        {repo.tags.length > 4 && (
                          <span className="tag-chip tag-more">+{repo.tags.length - 4}</span>
                        )}
                      </div>
                    )}

                    <div className="repository-footer">
                      <div className="repository-stats">
                        <span className="stat-item">
                          <strong>{repo.commit_count || 0}</strong> commits
                        </span>
                        <span className="stat-item">
                          <strong>{repo.branch_count || 0}</strong> branches
                        </span>
                        <span className="stat-item">
                          <strong>{repo.version_count || 0}</strong> versions
                        </span>
                      </div>
                      {repo.license && (
                        <div className="repository-license">
                          {repo.license.name}
                        </div>
                      )}
                    </div>

                    {repo.owner && (
                      <div className="repository-owner">
                        <span>
                          by {repo.owner.username}
                          <CertifiedBadge user={repo.owner} />
                        </span>
                        <span className="repository-updated">
                          Updated {new Date(repo.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </a>
                  </div>
                  )
                })
              ) : (
                <div className="no-results">
                  <p>No repositories found matching your filters.</p>
                  <button onClick={clearFilters} className="clear-filters-btn">
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default BrowseRepositories
